import { useClientHasModule, useUserHasRole } from 'hooks/client';
import * as React from 'react';
import {
    BUDGET,
    BUDGET_USER,
    DECISION,
    DECISION_USER,
} from 'business/constants';
import StyPageContainer from 'ui/PageContainer/PageContainer';
import Flex from 'ui/Flex/Flex';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import HelpIcon from '@mui/icons-material/Help';
import { useTranslation } from 'react-i18next';
import FavouriteDashboards from './FavouriteDashboards/FavouriteDashboards';
import ViewedDashboards from './ViewedDashboards/ViewedDashboards';
import UnreadNotifications from './UnreadNotifications/UnreadNotifications';
import BudgetKeyDates from './BudgetKeyDates/BudgetKeyDates';
import BudgetStates from './BudgetStates/BudgetStates';
import BlogArticles from './BlogArticles/BlogArticles';

const StyHelpCenterButton = styled(Button, {
    name: 'StyHelpCenterButton',
})(({ theme }) => ({
    backgroundColor: theme.palette.lightGrey,
    padding: '16px',
    borderRadius: '48px',
    '& .MuiButton-startIcon': {
        marginLeft: 0,
    },
}));

const StyModuleFlex = styled(Flex, {
    name: 'StyDecisionFlex',
})(({ theme }) => ({
    marginRight: '390px',
    gap: '40px',
    [theme.breakpoints.down('lg')]: {
        marginRight: '0',
    },
}));

const StyResponsiveFlex = styled(Flex, {
    name: 'StyResponsiveFlex',
})(({ theme }) => ({
    width: '100%',
    [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
        alignItems: 'start',
        gap: '12px',
        '& .MuiTypography-h1': {
            fontSize: '2rem',
        },
    },
    [theme.breakpoints.up('lg')]: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& .MuiTypography-h1': {
            fontSize: '3rem',
        },
    },
}));

const Home = () => {
    const isDecisionUser = useUserHasRole(DECISION_USER);
    const isBudgetUser = useUserHasRole(BUDGET_USER);
    const bindHasDecision = useClientHasModule(DECISION);
    const bindHasBudget = useClientHasModule(BUDGET);

    const hasBothModule =
        isDecisionUser && bindHasDecision && isBudgetUser && bindHasBudget;

    const { t } = useTranslation();

    const firstModule = (): JSX.Element => {
        if (isDecisionUser && bindHasDecision) {
            return (
                <StyModuleFlex
                    direction="column"
                    flexGrow={1}
                    sx={{ marginRight: 5 }}
                >
                    <FavouriteDashboards title={t('home.favorites')} />
                    <ViewedDashboards title={t('home.viewed')} />
                </StyModuleFlex>
            );
        }

        if (isBudgetUser && bindHasBudget) {
            return (
                <StyModuleFlex
                    direction="column"
                    flexGrow={1}
                    sx={{ marginRight: 5 }}
                >
                    <BudgetKeyDates />
                    <BudgetStates />
                </StyModuleFlex>
            );
        }

        return <></>;
    };

    return (
        <StyPageContainer sx={{ p: '2.5rem' }}>
            <StyResponsiveFlex>
                <Typography variant="h4" color="primary" fontWeight="bold">
                    {t('home.title')}
                </Typography>
                {/* We use this className to anchor */}
                <StyHelpCenterButton
                    startIcon={<HelpIcon />}
                    className="pendo-resource-center"
                >
                    {t('header.resourceCenter')}
                </StyHelpCenterButton>
            </StyResponsiveFlex>
            <Flex sx={{ mt: 5, gap: '40px' }} direction="column">
                <Flex sx={{ position: 'relative' }}>
                    {firstModule()}
                    <Flex direction="column" gap={1}>
                        <Flex
                            sx={{ width: 350 }}
                            className="pendo-newsletter-div"
                        ></Flex>
                        <UnreadNotifications />
                    </Flex>
                </Flex>
                <Flex align="stretch" sx={{ gap: 4 }}>
                    {hasBothModule && (
                        <StyModuleFlex flexGrow={1} direction="column">
                            <BudgetKeyDates />
                            <BudgetStates />
                        </StyModuleFlex>
                    )}
                </Flex>
                <BlogArticles />
            </Flex>
        </StyPageContainer>
    );
};

export default Home;
